import { ApplyParamsRecordEffectSpecID, UpdateRecordEffectSpecID } from "../../database/behaviour/effects/GadgetEffectLiterals";
import { FieldType } from "../../type-system/FieldType";
import {
  ShopifyOrderKey,
  ShopifyShopKey,
  ShopifySubscriptionBillingAttemptKey,
  ShopifySubscriptionBillingAttemptToOrderKey,
  ShopifySubscriptionBillingAttemptToShopKey,
  ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
  ShopifySubscriptionContractKey,
} from "./keys";
import type { ShopifyApiVersion } from "./ShopifyApiVersion";
import { ShopifyDominion } from "./ShopifyDominion";
import type { ConnectionModelSpecVersion } from "./ShopifyMetadata";
import { buildConnectionSpec } from "./ShopifyMetadata";

const specs: ConnectionModelSpecVersion = {
  key: ShopifySubscriptionBillingAttemptKey,
  "2022-01": {
    name: "Shopify Subscription Billing Attempt",
    fields: [
      {
        name: "Completed At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "completedAt" } },
      },
      {
        name: "Error Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "errorCode", shopifyFieldKey: "error_code" } },
        configuration: {
          allowMultiple: false,
          options: [
            { name: "AUTHENTICATION_ERROR" },
            { name: "BUYER_CANCELED_PAYMENT_METHOD" },
            { name: "CUSTOMER_INVALID" },
            { name: "CUSTOMER_NOT_FOUND" },
            { name: "EXPIRED_PAYMENT_METHOD" },
            { name: "INVALID_PAYMENT_METHOD" },
            { name: "INVALID_SHIPPING_ADDRESS" },
            { name: "PAYMENT_METHOD_DECLINED" },
            { name: "PAYMENT_METHOD_NOT_FOUND" },
            { name: "PAYMENT_PROVIDER_IS_NOT_ENABLED" },
            { name: "TEST_MODE" },
            { name: "UNEXPECTED_ERROR" },
          ],
        },
      },
      {
        name: "Error Message",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "errorMessage", shopifyFieldKey: "error_message" } },
      },
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "id", shopifyFieldKey: "id" } },
      },
      {
        name: "Idempotency Key",
        fieldType: FieldType.String,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "idempotencyKey", shopifyFieldKey: "idempotency_key" } },
      },
      {
        name: "Next Action URL",
        fieldType: FieldType.URL,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "nextActionUrl" } },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionBillingAttemptToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "order", graphQLSelection: `id`, shopifyFieldKey: "order_id" },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Ready",
        fieldType: FieldType.Boolean,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "ready", shopifyFieldKey: "ready" } },
      },
      {
        name: "Shopify Created At",
        fieldType: FieldType.DateTime,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "createdAt", shopifyFieldKey: "created_at" } },
      },
      {
        name: "Subscription Contract",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "subscriptionContract",
            graphQLSelection: "id",
            shopifyFieldKey: "subscription_contract_id",
          },
        },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
      {
        name: "Shop",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionBillingAttemptToShopKey,
        configuration: {
          relatedModelKey: ShopifyShopKey,
        },
      },
    ],
    stateChart: {
      includeCRUD: true,
      actions: [
        {
          name: "Challenged",
          from: "created",
          to: "created",
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          name: "Failure",
          from: "created",
          to: "created",
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
        {
          name: "Success",
          from: "created",
          to: "created",
          allowActionDeletion: false,
          onRunEffects: [
            {
              specID: ApplyParamsRecordEffectSpecID,
            },
            {
              specID: UpdateRecordEffectSpecID,
            },
          ],
        },
      ],
    },
    syncConfig: {
      type: "graphql_sublist",
      resource: "subscriptionContract.billingAttempts",
      parent: "subscriptionContracts",
    },
  },
  "2022-04": {
    fields: [
      {
        name: "Origin Time",
        fieldType: FieldType.DateTime,
        contributorConfiguration: {
          [ShopifyDominion.id]: { graphQLFieldKey: "originTime" },
        },
      },
    ],
  },
  "2024-04": {
    fields: [
      {
        name: "Error Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: { [ShopifyDominion.id]: { graphQLFieldKey: "errorCode", shopifyFieldKey: "error_code" } },
        configuration: {
          allowMultiple: false,
          options: [
            { name: "AMOUNT_TOO_SMALL" },
            { name: "AUTHENTICATION_ERROR" },
            { name: "BUYER_CANCELED_PAYMENT_METHOD" },
            { name: "CARD_NUMBER_INCORRECT" },
            { name: "CUSTOMER_INVALID" },
            { name: "CUSTOMER_NOT_FOUND" },
            { name: "EXPIRED_PAYMENT_METHOD" },
            { name: "FRAUD_SUSPECTED" },
            { name: "INSUFFICIENT_FUNDS" },
            { name: "INVALID_CUSTOMER_BILLING_AGREEMENT" },
            { name: "INVALID_PAYMENT_METHOD" },
            { name: "INVALID_SHIPPING_ADDRESS" },
            { name: "INVENTORY_ALLOCATIONS_NOT_FOUND" },
            { name: "INVOICE_ALREADY_PAID" },
            { name: "PAYMENT_METHOD_DECLINED" },
            { name: "PAYMENT_METHOD_INCOMPATIBLE_WITH_GATEWAY_CONFIG" },
            { name: "PAYMENT_METHOD_NOT_FOUND" },
            { name: "PAYMENT_PROVIDER_IS_NOT_ENABLED" },
            { name: "PAYPAL_ERROR_GENERAL" },
            { name: "PURCHASE_TYPE_NOT_SUPPORTED" },
            { name: "TEST_MODE" },
            { name: "TRANSIENT_ERROR" },
            { name: "UNEXPECTED_ERROR" },
          ],
        },
      },
    ],
  },
  "2025-04": {
    fields: [
      {
        name: "ID",
        fieldType: FieldType.ID,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "id",
            shopifyFieldKey: "id",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
      },
      {
        name: "Error Code",
        fieldType: FieldType.Enum,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "errorCode",
            shopifyFieldKey: "error_code",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
        configuration: {
          allowMultiple: false,
          options: [
            { name: "AMOUNT_TOO_SMALL" },
            { name: "AUTHENTICATION_ERROR" },
            { name: "BUYER_CANCELED_PAYMENT_METHOD" },
            { name: "CARD_NUMBER_INCORRECT" },
            { name: "CUSTOMER_INVALID" },
            { name: "CUSTOMER_NOT_FOUND" },
            { name: "EXPIRED_PAYMENT_METHOD" },
            { name: "FRAUD_SUSPECTED" },
            { name: "INSUFFICIENT_FUNDS" },
            { name: "INSUFFICIENT_INVENTORY" },
            { name: "INVALID_CUSTOMER_BILLING_AGREEMENT" },
            { name: "INVALID_PAYMENT_METHOD" },
            { name: "INVALID_SHIPPING_ADDRESS" },
            { name: "INVENTORY_ALLOCATIONS_NOT_FOUND" },
            { name: "INVOICE_ALREADY_PAID" },
            { name: "PAYMENT_METHOD_DECLINED" },
            { name: "PAYMENT_METHOD_INCOMPATIBLE_WITH_GATEWAY_CONFIG" },
            { name: "PAYMENT_METHOD_NOT_FOUND" },
            { name: "PAYMENT_PROVIDER_IS_NOT_ENABLED" },
            { name: "PAYPAL_ERROR_GENERAL" },
            { name: "PURCHASE_TYPE_NOT_SUPPORTED" },
            { name: "TEST_MODE" },
            { name: "TRANSIENT_ERROR" },
            { name: "UNEXPECTED_ERROR" },
          ],
        },
      },
      {
        name: "Subscription Contract",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionBillingAttemptToSubscriptionContractKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "subscriptionContract",
            graphQLSelection: "id",
            shopifyFieldKey: "subscription_contract_id",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
        configuration: {
          relatedModelKey: ShopifySubscriptionContractKey,
        },
      },
      {
        name: "Ready",
        fieldType: FieldType.Boolean,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "ready",
            shopifyFieldKey: "ready",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
      },
      {
        name: "Order",
        fieldType: FieldType.BelongsTo,
        key: ShopifySubscriptionBillingAttemptToOrderKey,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "order",
            graphQLSelection: `id`,
            shopifyFieldKey: "order_id",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
        configuration: {
          relatedModelKey: ShopifyOrderKey,
        },
      },
      {
        name: "Idempotency Key",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "idempotencyKey",
            shopifyFieldKey: "idempotency_key",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
      },
      {
        name: "Error Message",
        fieldType: FieldType.String,
        contributorConfiguration: {
          [ShopifyDominion.id]: {
            graphQLFieldKey: "errorMessage",
            shopifyFieldKey: "error_message",
            availableInWebhook: [
              "subscription_billing_attempts/success",
              "subscription_billing_attempts/failure",
              "subscription_billing_attempts/challenged",
            ],
          },
        },
      },
    ],
  },
};

export const ShopifySubscriptionBillingAttempt = ({
  apiVersion,
  frameworkVersionConstraint,
}: {
  apiVersion: ShopifyApiVersion;
  frameworkVersionConstraint: string;
}) => buildConnectionSpec(specs, apiVersion, frameworkVersionConstraint);
